import { type BorderRadius } from '@smart/web-foundations';
import classnames from 'classnames';
import React from 'react';
import { useTheme } from '../../contexts';
import type { ExtendWithCommonProps } from '../../types';
import scss from './skeleton.module.scss';

export type SkeletonProps = ExtendWithCommonProps<{
  /** Skeleton's width. */
  width: string;
  /** Skeleton's height. */
  height: string;
  /** If true, display skeleton or its children. */
  loading?: boolean;
  /** Skeleton's border radius. */
  borderRadius?: BorderRadius;
  /** Child content. Rendered when loading is `false`. */
  children?: React.ReactNode;
}>;

/**
 * Used to display a loading indicator.
 * If not loading will render it's children.
 */
export const Skeleton = React.forwardRef<
  React.ElementRef<'div'>,
  SkeletonProps
>(
  (
    {
      height,
      width,
      loading = true,
      borderRadius,
      children,
      className,
      ...props
    }: SkeletonProps,
    ref,
  ) => {
    const { theme } = useTheme();
    const classes = classnames(
      scss.skeleton,
      {
        [scss[`border-radius-${borderRadius}`]]: borderRadius,
        [scss[`theme-${theme}`]]: theme,
      },
      className,
    );
    if (!loading) return children;
    return (
      <div
        ref={ref}
        role="alert"
        aria-live="polite"
        aria-busy="true"
        className={classes}
        style={{ height, width }}
        {...props}
      />
    );
  },
);

Skeleton.displayName = 'Skeleton';
